import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import AdBanner from "@/components/AdBanner";
import { useLang } from "@/context/LanguageContext";
import { CANTON_NAMES } from "@/lib/cantons";
import { ArrowLeft } from "lucide-react";

export default function CantonPage() {
  const { code } = useParams();
  const { t } = useLang();
  const name = CANTON_NAMES[code] || code;
  const { data: events = [], isLoading } = useQuery({ queryKey: ["cantonEvents", code], queryFn: async () => (await api.get("/events", { params: { canton: code, limit: 200 } })).data });

  const jsonLd = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: `Événements folk & médiévaux — canton de ${name}`,
    description: `Marchés médiévaux, marchés vikings, festivals païens et concerts folk rituels dans le canton de ${name}, Suisse.`,
  };

  return (
    <>
      <Seo title={`Canton de ${name}`} description={`Tous les événements médiévaux, vikings, païens et folk rituels dans le canton de ${name}, Suisse. Marchés, festivals et concerts près de chez vous.`} jsonLd={jsonLd} canonicalPath={`/canton/${code}`} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <Link to="/cantons" className="inline-flex items-center gap-1.5 text-stone-400 hover:text-amber-400 text-sm mb-6" data-testid="back-to-cantons"><ArrowLeft className="w-4 h-4" /> {t("browse_all_cantons")}</Link>
        <div className="flex items-center gap-4 mb-4 border-b border-amber-900/30 pb-5">
          <span className="w-14 h-14 rounded-xl bg-amber-950/40 border border-amber-800/40 flex items-center justify-center shrink-0 font-serif text-amber-400 text-xl">{code}</span>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-1">{t("filter_canton")}</div>
            <h1 className="font-serif text-3xl sm:text-5xl uppercase tracking-tight text-amber-100">Canton de {name}</h1>
          </div>
        </div>
        <p className="text-stone-400 max-w-3xl mb-8">Découvrez tous les marchés médiévaux, marchés vikings, festivals, événements païens, reconstitutions et concerts folk rituels du canton de {name}.</p>
        <AdBanner placement="canton" className="mb-10" />

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(3)].map((_, i) => <div key={i} className="h-72 bg-[#121418] border border-[#272A34] rounded-lg animate-pulse" />)}</div>
        ) : events.length === 0 ? (
          <div className="text-center py-24 text-stone-400 border border-dashed border-[#272A34] rounded-lg">{t("no_results")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{events.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}</div>
        )}
      </div>
    </>
  );
}
